import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Button, Checkbox, TextInput } from '../Common'
import { PhoneNumberInput } from '../Common/PhoneNumberInput'
import { useNls } from '../../nls/nlsProvider'
import type { NlsKey } from '../../nls/strings_nls'
import { claimJoinCode } from '../../services/joinCodeService'
import {
  confirmPhoneChangeCode,
  sendPhoneChangeCode,
} from '../../services/contactIdentityChangeService'
import { formatRoleLabel } from '../../utils/roleLabels'
import type { WorkspaceRole } from '../../types/workspace'

export type ClaimedJoinCodeInfo = {
  workspaceId: string
  workspaceName: string
  role: string
}

export type JoinWithCodeFormProps = {
  prefillCode?: string
  onClaimed?: (claimed: ClaimedJoinCodeInfo) => void
  onCancel?: () => void
}

type Step = 'CODE' | 'PHONE' | 'PHONE_OTP' | 'CONSENT'

const normalizeCode = (val: string): string => val.toUpperCase().replace(/[\s-]+/g, '')

export const JoinWithCodeForm = ({
  prefillCode = '',
  onClaimed,
  onCancel,
}: JoinWithCodeFormProps) => {
  const { t } = useNls()
  const [step, setStep] = useState<Step>('CODE')
  const [code, setCode] = useState(() => normalizeCode(prefillCode))
  const [pin, setPin] = useState('')

  // Guardian phone state
  const [phone, setPhone] = useState('')
  const [verificationId, setVerificationId] = useState<string | null>(null)
  const [otp, setOtp] = useState('')
  const [isPhoneSending, setIsPhoneSending] = useState(false)
  const [isPhoneConfirming, setIsPhoneConfirming] = useState(false)
  const recaptchaContainerRef = useRef<HTMLDivElement | null>(null)

  // Guardian consent state
  const [consentNoticeVersion, setConsentNoticeVersion] = useState<string | null>(null)
  const [consentAgreed, setConsentAgreed] = useState(false)
  const [parentScreenMode, setParentScreenMode] = useState<'CONSENT_CHECKBOX' | 'INFO_NOTICE'>('CONSENT_CHECKBOX')
  const [retentionDays, setRetentionDays] = useState(365)
  const [institutionName, setInstitutionName] = useState('')

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [claimed, setClaimed] = useState<ClaimedJoinCodeInfo | null>(null)

  // Clear PIN on unmount
  useEffect(() => {
    return () => {
      setPin('')
    }
  }, [])

  const handleClaimError = (caught: unknown) => {
    const errObj =
      typeof caught === 'object' && caught !== null ? (caught as Record<string, unknown>) : null
    const details =
      errObj && typeof errObj.details === 'object' && errObj.details !== null
        ? (errObj.details as Record<string, unknown>)
        : null
    const serverCode =
      (typeof details?.code === 'string' ? details.code : null) ||
      (typeof errObj?.code === 'string' ? errObj.code : null)

    if (serverCode === 'PHONE_VERIFICATION_REQUIRED') {
      setStep('PHONE')
      setErrorMessage(null)
      return
    }

    if (serverCode === 'CONSENT_REQUIRED') {
      const version =
        typeof details?.noticeVersion === 'string' && details.noticeVersion
          ? details.noticeVersion
          : null
      if (!version) {
        setPin('')
        setStep('CODE')
        setErrorMessage(t('NLSJoinWithCodeErrorInvalid'))
        return
      }
      setConsentNoticeVersion(version)
      const mode = details?.parentScreenMode === 'INFO_NOTICE' ? 'INFO_NOTICE' : 'CONSENT_CHECKBOX'
      setParentScreenMode(mode)
      const retention = typeof details?.retentionDays === 'number' ? details.retentionDays : 365
      setRetentionDays(retention)
      const institution =
        typeof details?.institutionName === 'string' && details.institutionName.trim()
          ? details.institutionName.trim()
          : ''
      setInstitutionName(institution)
      setStep('CONSENT')
      setErrorMessage(null)
      return
    }

    // Every other error clears the PIN at once
    setPin('')
    setStep('CODE')

    let errorKey: NlsKey
    if (serverCode === 'INVALID_CODE_OR_PIN') {
      errorKey = 'NLSJoinWithCodeErrorInvalid'
    } else if (serverCode === 'TOO_MANY_ATTEMPTS') {
      errorKey = 'NLSJoinWithCodeErrorTooManyAttempts'
    } else if (serverCode === 'EXPIRED') {
      errorKey = 'NLSJoinWithCodeErrorExpired'
    } else if (serverCode === 'REVOKED') {
      errorKey = 'NLSJoinWithCodeErrorRevoked'
    } else if (serverCode === 'LOCKED') {
      errorKey = 'NLSJoinWithCodeErrorLocked'
    } else if (serverCode === 'ALREADY_USED') {
      errorKey = 'NLSJoinWithCodeErrorAlreadyUsed'
    } else if (serverCode === 'ALREADY_MEMBER') {
      errorKey = 'NLSJoinWithCodeErrorAlreadyMember'
    } else if (serverCode === 'SEATS_FULL') {
      errorKey = 'NLSJoinWithCodeErrorSeatsFull'
    } else if (serverCode === 'NOT_AVAILABLE') {
      errorKey = 'NLSJoinWithCodeErrorNotAvailable'
    } else if (serverCode === 'GOOGLE_SIGN_IN_REQUIRED') {
      errorKey = 'NLSJoinWithCodeErrorGoogleSignIn'
    } else if (serverCode === 'LEARNER_FROZEN') {
      errorKey = 'NLSJoinWithCodeErrorLearnerFrozen'
    } else {
      errorKey = 'NLSJoinWithCodeErrorInvalid'
    }

    setErrorMessage(t(errorKey))
  }

  const handlePhoneError = (caught: unknown) => {
    const errObj =
      typeof caught === 'object' && caught !== null ? (caught as Record<string, unknown>) : null
    const errorCode = typeof errObj?.code === 'string' ? errObj.code : ''
    const msg = typeof errObj?.message === 'string' ? errObj.message : ''

    if (
      errorCode === 'auth/credential-already-in-use' ||
      errorCode === 'auth/account-exists-with-different-credential' ||
      msg.includes('already on your account')
    ) {
      setErrorMessage(t('NLSJoinWithCodeErrorPhoneInUse'))
    } else if (errorCode === 'auth/too-many-requests') {
      setErrorMessage(t('NLSJoinWithCodeErrorTooManyRequests'))
    } else if (
      errorCode === 'auth/invalid-verification-code' ||
      errorCode === 'auth/code-expired'
    ) {
      setErrorMessage(t('NLSJoinWithCodeErrorInvalidOtp'))
    } else if (
      msg.includes('international') ||
      errorCode === 'auth/invalid-phone-number'
    ) {
      setErrorMessage(t('NLSJoinWithCodeErrorInternationalPhone'))
    } else {
      setErrorMessage(msg || t('NLSJoinWithCodeErrorInvalid'))
    }
  }

  const executeClaim = async (consentInput?: {
    accepted?: boolean
    acknowledged?: boolean
    noticeVersion: string
  }) => {
    setIsSubmitting(true)
    setErrorMessage(null)

    try {
      const res = await claimJoinCode({
        code: normalizeCode(code),
        pin: pin.trim(),
        ...(consentInput ? { consent: consentInput } : {}),
      })
      setPin('')
      setClaimed(res)
      onClaimed?.(res)
    } catch (caught) {
      handleClaimError(caught)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCodeSubmit = async (e: FormEvent) => {
    e.preventDefault()
    const cleanCode = normalizeCode(code)
    const currentPin = pin.trim()
    if (!cleanCode || currentPin.length !== 6) {
      setErrorMessage(t('NLSJoinWithCodeErrorInvalid'))
      setPin('')
      return
    }

    await executeClaim()
  }

  const handleSendPhone = async () => {
    if (!recaptchaContainerRef.current) return
    setIsPhoneSending(true)
    setErrorMessage(null)
    try {
      const vId = await sendPhoneChangeCode(phone, recaptchaContainerRef.current)
      setVerificationId(vId)
      setStep('PHONE_OTP')
    } catch (caught) {
      handlePhoneError(caught)
    } finally {
      setIsPhoneSending(false)
    }
  }

  const handleConfirmPhone = async () => {
    if (!verificationId || otp.trim().length !== 6) return
    setIsPhoneConfirming(true)
    setErrorMessage(null)
    try {
      await confirmPhoneChangeCode(verificationId, otp.trim())
      // After phone verification succeeds, resubmit the same claim
      await executeClaim()
    } catch (caught) {
      handlePhoneError(caught)
    } finally {
      setIsPhoneConfirming(false)
    }
  }

  const handleConsentSubmit = async () => {
    if (!consentNoticeVersion) return
    if (parentScreenMode === 'INFO_NOTICE') {
      await executeClaim({
        acknowledged: true,
        noticeVersion: consentNoticeVersion,
      })
    } else {
      if (!consentAgreed) return
      await executeClaim({
        accepted: true,
        noticeVersion: consentNoticeVersion,
      })
    }
  }

  const handleGuardianCancel = () => {
    setPin('')
    setStep('CODE')
    setOtp('')
    setVerificationId(null)
    setConsentAgreed(false)
    setConsentNoticeVersion(null)
    setParentScreenMode('CONSENT_CHECKBOX')
    setRetentionDays(365)
    setInstitutionName('')
    setErrorMessage(null)
  }

  if (claimed) {
    const roleDisplay = formatRoleLabel(t, claimed.role as WorkspaceRole)
    return (
      <div data-testid="join-with-code-success" className="flex flex-col gap-3 py-2 text-center">
        <h3 className="text-lg font-semibold text-[var(--text-strong)]">
          {t('NLSJoinWithCodeSuccessTitle')}
        </h3>
        <p className="text-sm text-[var(--text)]">
          {t('NLSJoinWithCodeSuccessBody', {
            role: roleDisplay,
            workspaceName: claimed.workspaceName,
          })}
        </p>
        <div className="mt-2 inline-flex flex-col items-center justify-center gap-1 rounded-md bg-[var(--surface-sunken)] p-3">
          <strong
            data-testid="join-claimed-workspace-name"
            className="text-base text-[var(--text-strong)]"
          >
            {claimed.workspaceName}
          </strong>
          <span
            data-testid="join-claimed-role"
            className="text-xs uppercase tracking-wider text-[var(--muted)]"
          >
            {roleDisplay}
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Invisible reCAPTCHA container for phone auth */}
      <div ref={recaptchaContainerRef} id="guardian-phone-recaptcha" className="hidden" />

      {step === 'CODE' && (
        <form onSubmit={handleCodeSubmit} className="flex flex-col gap-4" noValidate>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="join-code-field" className="text-sm font-medium text-[var(--text)]">
              {t('NLSJoinWithCodeCodeLabel')}
            </label>
            <TextInput
              id="join-code-field"
              aria-label={t('NLSJoinWithCodeCodeLabel')}
              value={code}
              onChange={(val) => setCode(normalizeCode(val))}
              placeholder={t('NLSJoinWithCodeCodePlaceholder')}
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              disabled={isSubmitting}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="join-pin-field" className="text-sm font-medium text-[var(--text)]">
              {t('NLSJoinWithCodePinLabel')}
            </label>
            <TextInput
              id="join-pin-field"
              aria-label={t('NLSJoinWithCodePinLabel')}
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              autoComplete="off"
              value={pin}
              onChange={(val) => setPin(val.replace(/\D/g, '').slice(0, 6))}
              placeholder={t('NLSJoinWithCodePinPlaceholder')}
              disabled={isSubmitting}
            />
          </div>

          {errorMessage && (
            <div
              role="alert"
              className="rounded-[var(--radius-ctl-sm)] bg-[color-mix(in_srgb,var(--danger)_12%,transparent)] p-2.5 text-xs font-medium text-[color-mix(in_srgb,var(--danger)_60%,var(--text-strong))]"
            >
              {errorMessage}
            </div>
          )}

          <div className="mt-2 flex items-center justify-end gap-2">
            {onCancel && (
              <Button
                variant="secondary"
                size="md"
                type="button"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                {t('NLSCommonActionCancel')}
              </Button>
            )}
            <Button
              variant="primary"
              size="md"
              type="submit"
              loading={isSubmitting}
              disabled={isSubmitting || !code || pin.length !== 6}
            >
              {isSubmitting ? t('NLSJoinWithCodeSubmitting') : t('NLSJoinWithCodeSubmit')}
            </Button>
          </div>
        </form>
      )}

      {(step === 'PHONE' || step === 'PHONE_OTP') && (
        <div className="flex flex-col gap-4">
          <div>
            <h3 className="text-base font-semibold text-[var(--text-strong)]">
              {t('NLSJoinWithCodePhoneStepTitle')}
            </h3>
            <p className="mt-1 text-xs text-[var(--muted)]">
              {t('NLSJoinWithCodePhoneStepDescription')}
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <PhoneNumberInput
              value={phone}
              onChange={setPhone}
              disabled={isPhoneSending || isPhoneConfirming || isSubmitting}
            />
          </div>

          {step === 'PHONE_OTP' && (
            <div className="flex flex-col gap-1.5">
              <label htmlFor="guardian-otp-input" className="text-sm font-medium text-[var(--text)]">
                {t('NLSJoinWithCodeOtpLabel')}
              </label>
              <TextInput
                id="guardian-otp-input"
                aria-label={t('NLSJoinWithCodeOtpLabel')}
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={otp}
                onChange={(val) => setOtp(val.replace(/\D/g, '').slice(0, 6))}
                placeholder={t('NLSJoinWithCodeOtpPlaceholder')}
                disabled={isPhoneConfirming || isSubmitting}
              />
            </div>
          )}

          {errorMessage && (
            <div
              role="alert"
              className="rounded-[var(--radius-ctl-sm)] bg-[color-mix(in_srgb,var(--danger)_12%,transparent)] p-2.5 text-xs font-medium text-[color-mix(in_srgb,var(--danger)_60%,var(--text-strong))]"
            >
              {errorMessage}
            </div>
          )}

          <div className="mt-2 flex items-center justify-end gap-2">
            <Button
              variant="secondary"
              size="md"
              type="button"
              onClick={handleGuardianCancel}
              disabled={isPhoneSending || isPhoneConfirming || isSubmitting}
            >
              {t('NLSCommonActionCancel')}
            </Button>

            {step === 'PHONE' ? (
              <Button
                variant="primary"
                size="md"
                type="button"
                onClick={handleSendPhone}
                loading={isPhoneSending}
                disabled={isPhoneSending || isSubmitting}
              >
                {isPhoneSending ? t('NLSJoinWithCodeSendingCode') : t('NLSJoinWithCodeSendCode')}
              </Button>
            ) : (
              <Button
                variant="primary"
                size="md"
                type="button"
                onClick={handleConfirmPhone}
                loading={isPhoneConfirming || isSubmitting}
                disabled={isPhoneConfirming || isSubmitting || otp.trim().length !== 6}
              >
                {isPhoneConfirming || isSubmitting
                  ? t('NLSJoinWithCodeConfirmingPhone')
                  : t('NLSJoinWithCodeConfirmPhone')}
              </Button>
            )}
          </div>
        </div>
      )}

      {step === 'CONSENT' && (
        <div className="flex flex-col gap-4">
          <div>
            <h3 className="text-base font-semibold text-[var(--text-strong)]">
              {t('NLSEduConsentNoticeTitle')}
            </h3>
            <p className="mt-2 text-xs leading-relaxed text-[var(--text)]">
              {t('NLSEduConsentNoticeBodyWithRetention', {
                institutionName: institutionName || t('NLSEduConsentDefaultInstitutionName'),
                retentionDays,
              })}
            </p>
          </div>

          {parentScreenMode === 'CONSENT_CHECKBOX' && (
            <div className="mt-1">
              <Checkbox
                id="guardian-consent-agree"
                checked={consentAgreed}
                onChange={setConsentAgreed}
                label={t('NLSEduConsentNoticeCheckboxLabel', {
                  institutionName: institutionName || t('NLSEduConsentDefaultInstitutionName'),
                })}
                disabled={isSubmitting}
              />
            </div>
          )}

          {errorMessage && (
            <div
              role="alert"
              className="rounded-[var(--radius-ctl-sm)] bg-[color-mix(in_srgb,var(--danger)_12%,transparent)] p-2.5 text-xs font-medium text-[color-mix(in_srgb,var(--danger)_60%,var(--text-strong))]"
            >
              {errorMessage}
            </div>
          )}

          <div className="mt-2 flex items-center justify-end gap-2">
            <Button
              variant="secondary"
              size="md"
              type="button"
              onClick={handleGuardianCancel}
              disabled={isSubmitting}
            >
              {t('NLSCommonActionCancel')}
            </Button>
            {parentScreenMode === 'INFO_NOTICE' ? (
              <Button
                variant="primary"
                size="md"
                type="button"
                data-testid="notice-acknowledge-btn"
                onClick={handleConsentSubmit}
                loading={isSubmitting}
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? t('NLSJoinWithCodeConsentContinuing')
                  : t('NLSEduNoticeAcknowledgeBtn')}
              </Button>
            ) : (
              <Button
                variant="primary"
                size="md"
                type="button"
                data-testid="consent-continue-btn"
                onClick={handleConsentSubmit}
                loading={isSubmitting}
                disabled={!consentAgreed || isSubmitting}
              >
                {isSubmitting
                  ? t('NLSJoinWithCodeConsentContinuing')
                  : t('NLSJoinWithCodeConsentContinue')}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
